import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    FormControl,
    FormLabel,
    IconButton,
    Option,
    Select,
    Sheet,
    Stack,
    Typography,
} from '@mui/joy';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getEvents } from '../services/observabilityAdminApi';
import EventRow from '../components/telemetry/EventRow';
import { PROJECT_OPTIONS } from '../utils/projectDisplayName';

const WINDOW_PRESETS = [
    { value: 15, label: '15m' },
    { value: 60, label: '1h' },
    { value: 180, label: '3h' },
    { value: 1440, label: '24h' },
];

export default function HubEventsPage() {
    const { hubId } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();

    const [windowMinutes, setWindowMinutes] = useState(60);
    const [timeWindowEnd, setTimeWindowEnd] = useState(Date.now());
    const timeWindowStart = timeWindowEnd - windowMinutes * 60 * 1000;

    const [importance, setImportance] = useState('');
    const [project, setProject] = useState('');
    const [events, setEvents] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextBeforeId, setNextBeforeId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    const loadEvents = useCallback(async ({ append = false, beforeId = null } = {}) => {
        if (!session?.accessToken || !session?.vendorId) return;

        if (append) setLoadingMore(true);
        else setLoading(true);
        setError(null);

        try {
            const params = {
                from: timeWindowStart,
                to: timeWindowEnd,
                limit: 50,
            };
            if (importance) params.importance = importance;
            if (project.trim()) params.project = project.trim();
            if (beforeId != null) params.before_id = beforeId;

            const response = await getEvents(hubId, params, session);
            const items = response.items || [];
            setEvents((prev) => (append ? [...prev, ...items] : items));
            setHasMore(Boolean(response.has_more));
            setNextBeforeId(response.next_before_id ?? null);
        } catch (err) {
            setError(err.message || String(err));
            if (!append) setEvents([]);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [hubId, session, timeWindowStart, timeWindowEnd, importance, project]);

    useEffect(() => {
        loadEvents({ append: false });
    }, [loadEvents]);

    const shiftWindow = (direction) => {
        const delta = windowMinutes * 60 * 1000;
        if (direction === 'past') {
            setTimeWindowEnd((prev) => prev - delta);
        } else {
            setTimeWindowEnd((prev) => Math.min(prev + delta, Date.now()));
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton variant="plain" onClick={() => navigate(`/hub/${hubId}`)}>
                    <ArrowBack />
                </IconButton>
                <Typography level="h3">Hub Events</Typography>
                <Chip size="sm" variant="soft">
                    Hub #{hubId}
                </Chip>
                <Box sx={{ flex: 1 }} />
                <IconButton variant="soft" onClick={() => loadEvents({ append: false })}>
                    <Refresh />
                </IconButton>
            </Stack>

            <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm' }}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography level="body-sm" fontWeight="lg">
                            Window
                        </Typography>
                        <ButtonGroup size="sm">
                            {WINDOW_PRESETS.map((preset) => (
                                <Button
                                    key={preset.value}
                                    variant={windowMinutes === preset.value ? 'solid' : 'outlined'}
                                    onClick={() => {
                                        setWindowMinutes(preset.value);
                                        setTimeWindowEnd(Date.now());
                                    }}
                                >
                                    {preset.label}
                                </Button>
                            ))}
                        </ButtonGroup>
                        <Button size="sm" variant="outlined" onClick={() => shiftWindow('past')}>
                            ◀ Older
                        </Button>
                        <Button size="sm" variant="outlined" onClick={() => shiftWindow('future')}>
                            Newer ▶
                        </Button>
                        <Typography level="body-xs" textColor="neutral.500">
                            {new Date(timeWindowStart).toLocaleString()} –{' '}
                            {new Date(timeWindowEnd).toLocaleString()}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControl size="sm" sx={{ minWidth: 160 }}>
                            <FormLabel>Importance</FormLabel>
                            <Select
                                value={importance}
                                placeholder="All"
                                onChange={(_, value) => setImportance(value || '')}
                            >
                                <Option value="">All</Option>
                                <Option value="critical">critical</Option>
                                <Option value="high">high</Option>
                                <Option value="medium">medium</Option>
                                <Option value="normal">normal</Option>
                            </Select>
                        </FormControl>
                        <FormControl size="sm" sx={{ minWidth: 180 }}>
                            <FormLabel>Project</FormLabel>
                            <Select
                                value={project}
                                placeholder="All"
                                onChange={(_, value) => setProject(value || '')}
                            >
                                <Option value="">All</Option>
                                {PROJECT_OPTIONS.map((opt) => (
                                    <Option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button size="sm" onClick={() => loadEvents({ append: false })}>
                                Apply filters
                            </Button>
                        </Box>
                    </Stack>
                </Stack>
            </Sheet>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            ) : events.length === 0 ? (
                <Alert color="neutral" variant="soft">
                    No events uploaded for this range
                </Alert>
            ) : (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                    {events.map((event) => (
                        <EventRow key={event.id} event={event} />
                    ))}
                    {hasMore && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Button
                                loading={loadingMore}
                                variant="soft"
                                onClick={() => loadEvents({ append: true, beforeId: nextBeforeId })}
                                disabled={nextBeforeId == null}
                            >
                                Load older
                            </Button>
                        </Box>
                    )}
                </Sheet>
            )}
        </Box>
    );
}
